import React from 'react';
import { Box, Flex, EventCard, Tag, StatusLabel, Pagination } from '@cowsquare/design';
import styled, { css } from 'styled-components';
import { Query, graphql } from 'react-apollo';
import gql from 'graphql-tag';
import * as R from 'ramda';
import { navigate } from '@reach/router';
import { Link } from 'gatsby';
import { usePagesBindingUrl, useFilterCategorys, useAdsQuery } from '../../../../utils/hooks';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import { FilterTags } from '../../../FilterTags';
import { Advertisements } from '../../../Advertisements';
import { getQueryObject } from '../../../../utils/getQueryObject';
import { getCategoryClause } from '../../../../utils/getWhereClause';
import { getDoctorId } from '../../../../utils/doctorUtils';
import { GET_USER_INFO, GET_HKDU_USER_INFO } from '../../../../apollo/graphql/user';

const PAGE_SIZE = 21;
const QuizCardBox = styled(Box).attrs({
  width: [1, 1 / 3],
  px: 4,
  pb: 5,
})`
  ${props =>
    props.expired &&
    css`
      opacity: 0.5;
    `}
`;

const GET_CME_QUIZZES = gql`
  query getCMEQuizzes(
    $limit: Int
    $offset: Int
    $where: hkdutest_cme_quizzes_safe_bool_exp
    $doctor_id: uuid!
  ) {
    categorys: hkdutest_cme_quiz_category {
      name
    }

    total: hkdutest_cme_quizzes_safe_aggregate(where: $where) {
      aggregate {
        count
      }
    }

    list: hkdutest_cme_quizzes_safe(
      limit: $limit
      order_by: { end_at: desc }
      offset: $offset
      where: $where
    ) {
      id
      name
      is_end
      is_publish
      questions {
        id
        answer_record(where: { doctor_id: { _eq: $doctor_id } }) {
          answer
        }
      }
      end_at
      point
      category {
        name
      }
    }
  }
`;

const QuizList = ({ location, uri, HKDUUserInfo }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);
  const [category, handleCategoryChange] = useFilterCategorys(queryObject, uri);
  const { ads } = useAdsQuery(uri);
  const whereClause = getCategoryClause(category);

  return (
    <Query
      query={GET_CME_QUIZZES}
      variables={{
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        where: whereClause,
        doctor_id: getDoctorId(HKDUUserInfo),
      }}
    >
      {({ data }) => {
        const list = R.propOr(0, 'list', data);
        const categorys = R.pipe(R.propOr([], 'categorys'), R.pluck('name'), R.prepend('All'))(data);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);
        const showPagination = total > PAGE_SIZE;

        return (
          <Section>
            <Advertisements ads={ads} />
            <Box px={[5, 0]} py={8}>
              <Box mb={5}>
                <Title m={0} mb={[5, 0]}>
                  CME Quizzes
                </Title>
              </Box>
              <Box mb={6}>
                <FilterTags value={category} tags={categorys} onChange={handleCategoryChange} />
              </Box>
              <Box mt={5} mx={-4}>
                <Flex flexWrap="wrap">
                  {R.map(item => {
                    const endAt = R.propOr('', 'end_at', item);
                    const categoryName = R.pathOr('', ['category', 'name'])(item);
                    const submitted = R.pipe(
                      R.propOr([], 'questions'),
                      R.pluck('answer_record'),
                      R.isEmpty,
                      R.not
                    )(item);
                    return (
                      <QuizCardBox key={item.id} expired={item.is_end}>
                        <EventCard
                          as={Link}
                          to={`${uri}/${item.id}`}
                          height={225}
                          date={endAt}
                          title={item.name}
                          status={submitted && <StatusLabel type="success">Submitted</StatusLabel>}
                          tags={
                            categoryName && (
                              <Tag
                                key={categoryName}
                                onClick={event => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  navigate(`${uri}?category=${categoryName}`);
                                }}
                              >
                                {categoryName}
                              </Tag>
                            )
                          }
                          cardInfoData={[
                            {
                              key: item.point,
                              icon_name: 'point',
                              text: `${item.point} Point`,
                            },
                          ]}
                        />
                      </QuizCardBox>
                    );
                  }, list)}
                </Flex>
              </Box>
              {/* TODO: Pagination 直接由外部決定要不要顯示 https://github.com/cowsquare/cowsquare-mono/pull/58#discussion_r302190214*/}
              {showPagination && (
                <Box mt={8}>
                  <Flex justifyContent="center">
                    <Pagination current={page} total={total} pageSize={PAGE_SIZE} onChange={onPageChange} />
                  </Flex>
                </Box>
              )}
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default R.compose(
  graphql(GET_USER_INFO, { name: 'userInfo' }),
  graphql(GET_HKDU_USER_INFO, {
    name: 'HKDUUserInfo',
    options: ({ userInfo }) => ({
      variables: {
        id: R.pathOr(null, ['user', 'id'], userInfo),
      },
    }),
  })
)(QuizList);
